<template>
  <div>
    <popup-hint hint="purchaseorders">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Purchase orders">
          <template v-slot:left v-if="purchaseOrdersRequiringApproval">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{purchaseOrdersRequiringApproval}} purchase order{{purchaseOrdersRequiringApproval>1?'s':''}} requires approval
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.from=null;filters.to=null;filters.supplierId=null;itemId='';filters.search=null;filters.closed='2';filters.approved='0';filters.confirmed='2'})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
          <template v-slot:right>
            <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left>
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">mdi-tune-variant</v-icon>
              </template>
              <purchasing-purchase-orders-preferences/>
            </v-menu>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Purchase orders can be created to request items from suppliers.
      </template>
    </popup-hint>

    <purtrak-table name="pos" objectType="purchase orders" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="purchaseOrders2" v-model="selected"
                   fields="id,date,due,number,vendorId,vendorName,privateNote,total,closed,confirmed,approved,sent,received,email,currency,attachmentURL"
                   :listFilters="listFilters"
                   :item-class="po => itemClass(po)"
                   @dblclick="po => $router.push({name:'PurchasingPurchaseOrdersEdit',params:{poId:po.id}})"
                   @itemExpanded="({item, value}) => expandPO(item, value)"
                   title="Purchase orders"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.from?dateDisplay:'',
                                           filters.closed!='2'?(filters.closed?'Open':'Closed'):'',
                                           filters.approved!='2'?(filters.approved?'Approved':'Not approved'):'',
                                           filters.sent!='2'?(filters.sent?'Sent':'Not sent'):'',
                                           filters.confirmed!='2'?(filters.confirmed?'Confirmed':'Not confirmed'):'',
                                           filters.supplierId?filters.supplierName:'']}"
                   download-endpoint="purchaseOrdersExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.from=null;filters.to=null;filters.closed='2';filters.approved='2';filters.confirmed='2';filters.search='';filters.customerId=null">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'vendorName'},{text:'Number',value:'number'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{text:'All',value:'2'},{divider:true},{text:'Open',value:'1'},{text:'Closed',value:'0'}]" v-model="filters.closed"/>
          </v-list-item>
          <v-list-item v-if="settings.poRequireApproval">
            <v-autocomplete label="Approved" dense outlined :items="[{text:'All',value:'2'},{divider:true},{text:'Approved',value:'1'},{text:'Not approved',value:'0'}]" v-model="filters.approved"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Confirmed" dense outlined :items="[{text:'All',value:'2'},{divider:true},{text:'Confirmed',value:'1'},{text:'Not confirmed',value:'0'}]" v-model="filters.confirmed"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Item" dense outlined :items="items" item-text="name" item-value="id" v-model="filters.itemId"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search='';filters.customerId=null">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.closed!='2'" close small class="ml-1" @click:close="filters.closed='2'">{{filters.closed=='1'?'Open':'Closed'}}</v-chip>
          <v-chip v-show="filters.approved!='2'" close small class="ml-1" @click:close="filters.approved='2'">{{filters.approved=='1'?'Approved':'Not approved'}}</v-chip>
          <v-chip v-show="filters.sent!='2'" close small class="ml-1" @click:close="filters.sent='2'">{{filters.sent=='1'?'Sent':'Not sent'}}</v-chip>
          <v-chip v-show="filters.confirmed!='2'" close small class="ml-1" @click:close="filters.confirmed='2'">{{filters.confirmed=='1'?'Confirmed':'Not confirmed'}}</v-chip>
          <v-chip v-show="filters.supplierId" close small class="ml-1" @click:close="filters.supplierId=null">{{filters.supplierName}}</v-chip>
          <v-chip v-show="filters.itemId" close small class="ml-1" @click:close="filters.itemId=''">{{itemName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'PurchasingPurchaseOrdersEditNew'}"><v-list-item-title>Create purchase order</v-list-item-title></v-list-item>
        </template>
        <template v-if="company.allowRecurringTransactions">
          <v-divider/>
          <v-list-item :to="{name:'RecurringTransactions',query:{targetType:'PO'}}"><v-list-item-title>Recurring purchase orders</v-list-item-title></v-list-item>
        </template>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-menu offset-y top>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" v-on="on">Batch commands</v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="print()"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item @click="deleteMultiple=true;showDelete=true"><v-list-item-title>Delete</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.date="{value}">
        {{value | formatDate}}
      </template>
      <template v-slot:item.confirmed="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.closed="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.approved="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.received="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.due="{value}">
        {{value | formatDate}}
      </template>
      <template v-slot:item.vendorNameNote="{item,printing}">
        {{item.vendorName}}
        <div v-if="!printing" class="text-caption">{{item.privateNote}}</div>
      </template>
      <template v-slot:item.total="{item,value}">
        {{item.currency===homeCurrency?'':item.currency}} {{value | currency}}
      </template>
      <template v-slot:item.status="{item,printing}">
        <template>{{status(item)}}</template>
        <div v-if="!printing && !item.closed">
          <v-btn v-if="item.approved" rounded color="primary" x-small @click="receive(item)">Receive</v-btn>
          <div v-else-if="!item.vendorId" class="text-caption">(Supplier required)</div>
          <v-btn v-else-if="$store.getters.company.permissions.includes('po.approve')" rounded color="warning" x-small @click="approve(item)">Approve</v-btn>
        </div>
      </template>
      <template v-slot:header.attachmentURL>
        <v-icon>mdi-paperclip</v-icon>
      </template>
      <template v-slot:item.attachmentURL="{value}">
        <v-icon v-if="value" class="green--text" @click="open(value)">mdi-paperclip</v-icon>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'PurchasingPurchaseOrdersEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Purchase order operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <template v-if="!item.approved && $store.getters.company.permissions.includes('po.approve')">
              <v-list-item @click="approve(item)"><v-list-item-title>Approve</v-list-item-title></v-list-item>
            </template>
            <template v-else-if="!item.closed">
              <v-list-item v-if="item.approved" @click="receive(item)"><v-list-item-title>Receive</v-list-item-title></v-list-item>
              <v-list-item v-if="!item.confirmed && item.approved" @click="confirm(item)"><v-list-item-title>Confirm</v-list-item-title></v-list-item>
              <v-list-item @click="close(item)"><v-list-item-title>Close</v-list-item-title></v-list-item>
              <v-divider/>
            </template>
            <v-list-item @click="print(item)"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-list-item v-if="item.email" :href="`mailto:${item.email}?subject=PO from ${company.name}`"><v-list-item-title>Create email</v-list-item-title></v-list-item>
            <v-list-item v-if="connectedToAccounting && settings.poCreateInQBO" @click="printQBO(item)"><v-list-item-title>View from QBO</v-list-item-title></v-list-item>
            <v-list-item v-if="connectedToAccounting && settings.poCreateInQBO" @click="send(item)"><v-list-item-title>Send from QBO</v-list-item-title></v-list-item>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'PurchasingPurchaseOrdersEdit',params:{poId:item.id}}"><v-list-item-title>Edit purchase order</v-list-item-title></v-list-item>
              <v-list-item :to="{name:'PurchasingPurchaseOrdersEditNew',params:{copyPOId:item.id}}"><v-list-item-title>Copy purchase order</v-list-item-title></v-list-item>
              <v-list-item @click="updatePrivateNote(item)"><v-list-item-title>Update private note</v-list-item-title></v-list-item>
              <v-list-item @click="po=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete purchase order</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4" :class="expansionPanelClass">
          <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:70%">
            <thead>
              <tr>
                <th :class="expansionPanelClass" width="35%">Description</th>
                <th class="text-right" :class="expansionPanelClass" width="20%">Quantity</th>
                <th class="text-right" :class="expansionPanelClass" width="15%">Received</th>
                <th class="text-right" :class="expansionPanelClass" width="15%">Unit price</th>
                <th class="text-right" :class="expansionPanelClass" width="15%">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line of item.lines" :key="line.key">
                <td class="text-caption">{{line.description}}</td>
                <td class="text-caption text-right">
                  {{line.quantity| number(line.precision)}} {{line.units}}
                </td>
                <td class="text-caption text-right">
                  <template>{{line.receivedQuantity | number(line.precision)}}</template>
                </td>
                <td class="text-caption text-right">${{line.unitPrice | number(4)}}</td>
                <td class="text-caption text-right">{{line.amount | currency}}</td>
              </tr>
              <template v-for="tax of item.taxLines">
                <tr :key="tax.id">
                  <td class="text-caption text-right" colspan="4">
                    {{tax.name}} @ {{tax.percent}}% on {{tax.onAmount | currency}}
                  </td>
                  <td class="text-caption text-right">{{tax.amount | currency}}</td>
                </tr>
              </template>
            </tbody>
          </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="deleteMultiple?'ALL selected purchase orders':'this purchase order'" @delete="performDelete(po.id)"/>
    <component :is="dialog" v-bind="dialogProperties" :purchaseOrder="po" :purchaseOrders="selected" @done="dialog = null"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'
import printHtml from '@/mixins/printHtml'
import PurchasingPurchaseOrdersPreferences from '@/views/purchasing/purchaseOrders/PurchasingPurchaseOrdersPreferences'

export default {
  name: 'PurchasingPurchaseOrders',
  mixins: [printHtml,performDelete],
  components: {PurchasingPurchaseOrdersPreferences},
  data() {
    return {
      purchaseOrdersRequiringApproval: 0,
      filters: {
        from: null,
        to: null,
        supplierId: null,
        itemId: '',
        confirmed: '2',
        closed: '2',
        approved: '2',
        sent: '2',
        search: '',
        searchField: 'vendorName',
      },
      items: [{name:'All',id:''},{divider:true}],
      showStatus: false,
      settings: {},
      dateDisplay: '',
      searchDisplay: '',
      dialog: null,
      dialogProperties: {},
      po: {},
      showDelete: false,
      deleteMultiple: false,
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'number', visible:true},
                       { value: 'vendorName', visible:true},
                       { value: 'vendorNameNote', visible:false},
                       { value: 'total', visible:true},
                       { value: 'due', visible:false},
                       { value: 'privateNote', visible:false},
                       { value: 'confirmed', visible:false},
                       { value: 'closed', visible:false},
                       { value: 'approved', visible:false},
                       { value: 'received', visible:false},
                       { value: 'attachmentURL', visible:true,excludeFromExport:true},
                       { value: 'status', visible:true,excludeFromExport:true}],
      headers: [{ text: 'DATE', value: 'date', width:'15%',sortable:true},
                { text: 'DUE', value: 'due', width:'15%',sortable:true},
                { text: 'NUMBER', value: 'number' ,width:'10%',sortable:true},
                { text: 'SUPPLIER', value: 'vendorName' ,width:'25%',sortable:true},
                { text: 'SUPPLIER/NOTE', value: 'vendorNameNote' ,width:'25%',sortable:true},
                { text: 'NOTE', value: 'privateNote' ,width:'25%',sortable:false},
                { text: 'TOTAL', value: 'total' ,width:'15%',sortable:true,align:'right'},
                { text: 'CONFRM', value: 'confirmed' ,width:'10%',sortable:true,align:'center'},
                { text: 'CLOSED', value: 'closed' ,width:'10%',sortable:true,align:'center'},
                { text: 'APPRVD', value: 'approved' ,width:'10%',sortable:true,align:'center'},
                { text: 'RECVD', value: 'received' ,width:'10%',sortable:true,align:'center'},
                { text: 'ATTACHMENT', value: 'attachmentURL' ,width:'5%',sortable:false,print:false},
                { text: 'STATUS', value: 'status' ,width:'20%',sortable:false,align:'center'}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('buying.write');
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    homeCurrency() {
      return this.company.settings.homeCurrency;
    },
    itemName() {
      return this.items.find(item => item.id === this.filters.itemId)?.name;
    },
    listFilters() {
      const filters = [];
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.supplierId !== null)
        filters.push({field:'vendorId', comparator:'eq', value:this.filters.supplierId});
      if (this.filters.itemId !== '')
        filters.push({field:'itemId', comparator:'eq', value:this.filters.itemId});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});
      if (this.filters.closed != '2')
        filters.push({field:'closed', comparator:'eq', value:this.filters.closed == '0' ? 'true' : 'false'});
      if (this.filters.sent != '2')
        filters.push({field:'sent', comparator:'eq', value:this.filters.sent == '1' ? 'true' : 'false'});
      if (this.filters.confirmed != '2')
        filters.push({field:'confirmed', comparator:'eq', value:this.filters.confirmed == '1' ? 'true' : 'false'});
      if (this.filters.approved != '2')
        filters.push({field:'approved', comparator:'eq', value:this.filters.approved == '1' ? 'true' : 'false'});

      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
    this.items.push(...await this.$store.dispatch('items'));

    const results = await this.$apiCall(`query PurchasingPurchaseOrders {
                                          purchaseOrderPreferences {poRequireApproval,poCreateInQBO}
                                         }`);
    if (!results.error) {
      this.settings = results.data.purchaseOrderPreferences;
    }

    await this.getStatus();
    if (!['PurchasingPurchaseOrdersEdit', 'PurchasingPurchaseOrdersEditNew'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.purchaseOrdersRequiringApproval && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    itemClass(po) {
      if (po.closed)
        return 'grey--text';
      else if (po.confirmed)
        return 'success--text';
      else if (!po.approved)
        return 'warning--text';
    },
    async getStatus() {
      this.loading = true;
      const results = await this.$apiCall(`query PurchasingPurchaseOrders {
                                                purchaseOrdersRequiringApproval
                                              }`);
      if (!results.error)
        this.purchaseOrdersRequiringApproval = results.data.purchaseOrdersRequiringApproval;
      this.loading = false;
    },
    async expandPO(item, value) {
      if (value && !item.lines) {
        this.loading = true;
        const results = await this.$apiCall(`query PurchasingPurchaseOrders($id:ID!) {
                                                purchaseOrder(id:$id) {
                                                  lines{description,quantity,receivedQuantity,unitPrice,amount,precision,units}
                                                  taxLines {id,name,amount,onAmount,percent}
                                                }
                                              }`,
                                            {id:item.id});
        this.$set(item, 'lines', results.data.purchaseOrder.lines);
        this.$set(item, 'taxLines', results.data.purchaseOrder.taxLines);
        let key = 1;
        item.lines.forEach(line => line.key = key++);
        this.loading = false;
      }
    },
    status(po) {
      if (po.closed)
        return 'Closed';
      if (!po.approved)
        return `Not approved`;
      if (po.received)
        return 'Open - partially received';
      if (po.confirmed)
        return 'Open - confirmed';
      if (po.sent)
        return 'Open - sent';
      return 'Open';
    },
    async approve(po) {
      const results = await this.$apiCall(`mutation PurchasingPurchaseOrders($id: ID!) {
                                              purchaseOrderApprove(id:$id) {
                                                approved
                                              }
                                            }`,
                                        {id:po.id});
      if (!results.error) {
        po.approved = results.data.purchaseOrderApprove.approved;
        this.$store.commit('setError', {type:'success',message:`Approved purchase order for ${po.vendorName}`});
        this.getStatus();
      }
    },
    async send(po) {
      if (!po.email) {
        this.$store.commit('setError', {type:'warning',message:`Cannot send this purchase order since is doesn't have an email address`});
        return;
      }

      this.loading = true;
      const results = await this.$apiCall(`mutation PurchasingPurchaseOrders($id: ID!) {
                                                purchaseOrderSend(id:$id) {
                                                  sent
                                                }
                                              }`,
                                          {id:po.id});
      this.loading = false;
      if (!results.error) {
        po.sent = results.data.purchaseOrderSend.sent;
        this.$store.commit('setError', {type:'success',message:`Purchase order sent from QuickBooks Online`});
      }
    },
    async confirm(po) {
      const results = await this.$apiCall(`mutation PurchasingPurchaseOrders($id: ID!) {
                                                purchaseOrderConfirm(id:$id) {
                                                  confirmed
                                                }
                                              }`,
                                          {id:po.id});
      if (!results.error) {
        po.confirmed = results.data.purchaseOrderConfirm.confirmed;
        this.$store.commit('setError', {type:'success',message:`Confirmed purchase order for ${po.vendorName}`});
      }
    },
    async close(po) {
      const results = await this.$apiCall(`mutation PurchasingPurchaseOrders($id: ID!) {
                                                purchaseOrderClose(id:$id) {
                                                  closed
                                                }
                                              }`,
                                          {id:po.id});
      if (!results.error) {
        po.closed = results.data.purchaseOrderClose.closed;
        this.$store.commit('setError', {type:'success',message:`Closed purchase order for ${po.vendorName}`});
      }
    },
    async printQBO(po) {

      this.loading = true;
      const results = await this.$apiCall(`query PurchasingPurchaseOrders($id: ID!) {
                                                purchaseOrderPrint(id:$id)
                                              }`,
                                          {id:po.id});
      this.loading = false;
      if (!results.error) {
        const win = window.open();
        if (!win) {
          this.$store.commit('setError', {type:'warning',message:`Your browser permissions must allow pop-up windows`});
          return;
        }
        const document = win.document;
        const obj = document.createElement('object');
        obj.style.width="100%";
        obj.style.height="100%";
        obj.type = 'application/pdf';
        obj.data = 'data:application/pdf;base64,' + results.data.purchaseOrderPrint;
        document.body.appendChild(obj);
        win.document.close();
        win.focus();
      }
    },
    print(po) {
      this.po = po;
      this.dialog = () => import(`@/views/purchasing/purchaseOrders/PurchasingPurchaseOrdersPrint`);
    },
    receive(po) {
      this.$router.push({name:'PurchasingInventoryReceiptsEdit',params:{poId:po.id}});
    },
    open(url) {
      const win = window.open(url);
      if (!win) {
        this.$store.commit('setError', {type:'warning',message:`Your browser permissions must allow pop-up windows`});
        return;
      }
    },
    updatePrivateNote(purchaseOrder) {
      this.dialogProperties = {item:purchaseOrder, operation:'purchaseOrderUpdatePrivateNote'};
      this.dialog = () => import(`@/components/UpdatePrivateNote`);
    }
  }
}
</script>